import { SearchInvoiceRequest, WorkflowStatus } from '../../../clients/quipli-service/invoice/invoice';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Location } from '../../../clients/quipli-service/location/location';
import { User, UserAddress } from '../../../clients/quipli-service/user/user';
import { Button } from '../../../components/form/button';
import { Typography } from '../../../components/typography/typography';
import Icon from '../../../components/icons/icon';
import { FiltersModal } from '../../../components/modal/filters-modal';
import { UserSearch } from '../../../components/user-search/user-search';
import { DateTime, Interval } from 'luxon';
import { DatePicker } from '../../../components/date-picker/date-picker';
import { SelectForm } from '../../../components/select-form/select-form';
import { debounce } from 'lodash';
import { Badge } from '../../../components/badge/badge';
import { OnAccountStatus } from '../../../clients/quipli-service/payment/on-account/on-account';
import { UserAddressSearch } from '../../../components/user-search/user-address-search';

type InvoiceStatus = 'Void' | 'Draft' | 'Payable' | 'Paid' | 'Past Due' | 'Pending Refund';
const INVOICE_STATUSES: InvoiceStatus[] = ['Void', 'Draft', 'Payable', 'Paid', 'Past Due', 'Pending Refund'];
const WORKFLOW_STATUSES: WorkflowStatus[] = [
  'Requests',
  'Approved',
  'On Rent',
  'Off Rent',
  'Completed',
  'Cancelled',
  'Quote',
];
const daysPastDueOptions = [
  { label: 'Current', value: 1 },
  { label: '30 days', value: 30 },
  { label: '60 days', value: 60 },
  { label: '90 days', value: 90 },
  { label: '120 days', value: 120 },
  { label: 'Specify date range for due date', value: -1 },
];
const onAccountOptions: { label: string; value: OnAccountStatus.Approved | 'other' }[] = [
  { label: 'Approved', value: OnAccountStatus.Approved },
  { label: 'Not Set Up', value: 'other' },
];

export const CollectionsFilters = ({
  searchRequest,
  setSearchRequest,
  locations,
  disabledFilters = [],
  activeFilters,
}: {
  searchRequest: SearchInvoiceRequest;
  setSearchRequest: Dispatch<SetStateAction<SearchInvoiceRequest>>;
  locations: Location[];
  disabledFilters?: (
    | 'customer'
    | 'address'
    | 'location'
    | 'dueDate'
    | 'date'
    | 'status'
    | 'search'
    | 'orderStatus'
    | 'daysPastDue'
    | 'onAccountStatus'
  )[];
  activeFilters: number;
}) => {
  const { getUserById, getUserAddress } = useAPI();
  const [filtersModalOpen, setFiltersModalOpen] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User>();
  const [selectedAddress, setSelectedAddress] = useState<UserAddress>();
  const [selectedLocation, setSelectedLocation] = useState<Location>();
  const [dateFrom, setDateFrom] = useState<DateTime>();
  const [dateTo, setDateTo] = useState<DateTime>();
  const [dueDateFrom, setDueDateFrom] = useState<DateTime>();
  const [dueDateTo, setDueDateTo] = useState<DateTime>();
  const [daysPastDue, setDaysPastDue] = useState<number>();
  const [selectedInvoiceStatus, setSelectedInvoiceStatus] = useState<InvoiceStatus[]>();
  const [selectedOrderStatus, setSelectedOrderStatus] = useState<WorkflowStatus>();
  const [selectedOnAccountStatus, setSelectedOnAccountStatus] = useState<
    OnAccountStatus.Approved | 'other' | undefined
  >();
  const [query, setQuery] = useState<string | undefined>(searchRequest.query);

  function onOpenFilters() {
    if (searchRequest.customerId !== selectedUser?.id) {
      if (searchRequest.customerId) {
        getUserById(searchRequest.customerId).then(setSelectedUser);

        if (searchRequest.addressId) {
          getUserAddress(searchRequest.customerId, searchRequest.addressId).then(setSelectedAddress);
        }
      } else {
        setSelectedUser(undefined);
      }

      if (!searchRequest.addressId) {
        setSelectedAddress(undefined);
      }
    }
    if (searchRequest.locationId !== selectedLocation?.id) {
      setSelectedLocation(locations.find((l) => l.id === searchRequest.locationId));
    }
    setQuery(searchRequest.query);
    setDateFrom(searchRequest.date?.from);
    setDateTo(searchRequest.date?.to);
    setDueDateFrom(searchRequest.dueDate?.from);
    setDueDateTo(searchRequest.dueDate?.to);
    setDaysPastDue(searchRequest.daysPastDue);
    setSelectedInvoiceStatus(searchRequest.invoiceStatusFilter);
    setSelectedOrderStatus(
      searchRequest.workflowStatusFilter !== undefined
        ? (searchRequest.workflowStatusFilter[0] as WorkflowStatus)
        : undefined,
    );
    setSelectedOnAccountStatus(searchRequest.onAccountStatus);
    setFiltersModalOpen(true);
  }

  function clearFilters() {
    setQuery('');
    setSearchRequest((previous) => {
      return {
        ...previous,
        query: undefined,
        customerId: undefined,
        addressId: undefined,
        locationId: undefined,
        date: undefined,
        dueDate: undefined,
        daysPastDue: undefined,
        invoiceStatusFilter: undefined,
        orderStatusFilter: undefined,
        orderReturnStatusFilter: undefined,
        workflowStatusFilter: undefined,
        onAccountStatus: undefined,
      };
    });
  }

  function onCancel() {
    setFiltersModalOpen(false);
  }

  function onConfirm() {
    setSearchRequest((prevState) => ({
      ...prevState,
      query: query || undefined,
      customerId: selectedUser?.id,
      addressId: selectedAddress?.id,
      locationId: selectedLocation?.id,
      date: {
        from: dateFrom?.setZone(locations.find(() => true)?.timezone, { keepLocalTime: true }),
        to: dateTo?.setZone(locations.find(() => true)?.timezone, { keepLocalTime: true }),
      },
      dueDate: {
        from: dueDateFrom?.setZone(locations.find(() => true)?.timezone, { keepLocalTime: true }),
        to: dueDateTo?.setZone(locations.find(() => true)?.timezone, { keepLocalTime: true }),
      },
      daysPastDue,
      invoiceStatusFilter: selectedInvoiceStatus,
      workflowStatusFilter: selectedOrderStatus === undefined ? [] : [selectedOrderStatus],
      onAccountStatus: selectedOnAccountStatus,
    }));
    setFiltersModalOpen(false);
  }

  const handleOnChangeQuery = useCallback(
    debounce((query) => {
      if (query !== searchRequest.query) {
        setSearchRequest((prev) => {
          return { ...prev, query };
        });
      }
    }, 500),
    [searchRequest],
  );

  useEffect(() => {
    handleOnChangeQuery(query);
  }, [query]);

  useEffect(() => {
    if (!!daysPastDue && (selectedInvoiceStatus ?? []).indexOf('Past Due') < 0) {
      setSelectedInvoiceStatus([...(selectedInvoiceStatus ?? []), 'Past Due']);
    }
  }, [daysPastDue]);

  return (
    <div className="flex gap-2 flex-wrap">
      {disabledFilters.indexOf('search') < 0 && (
        <div>
          <div className="search-bar w-full md:w-[548px]">
            <Icon className="ml-[12px]" icon="Search" color="quipli-black-500" />
            <input
              data-cy="search-invoices"
              type="text"
              onChange={({ target }) => setQuery(target.value)}
              value={query}
            />
          </div>
        </div>
      )}
      <Button
        className="button-secondary !border-0 h-[46px] relative"
        onClick={() => onOpenFilters()}
        data-cy="filter-order"
      >
        <Typography type="text-md" className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]">
          <Icon icon="FilterLines" stroke="var(--grey-700)" /> Filters
        </Typography>

        {activeFilters > 0 && (
          <Badge className="absolute -right-2 -top-2" text={`${activeFilters}`} size="small" type="yellow" />
        )}
      </Button>

      {activeFilters > 0 && (
        <Button className="button-secondary !border-0 h-[46px]" onClick={clearFilters} data-cy="clear-filter-order">
          <Typography type="text-md" className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]">
            Clear Filters
          </Typography>
        </Button>
      )}
      {filtersModalOpen && (
        <FiltersModal isOpen={filtersModalOpen} title="Filters" onConfirm={onConfirm} onCancel={onCancel}>
          <div className="flex flex-col gap-3">
            {disabledFilters?.indexOf('date') < 0 && (
              <div className="flex flex-col gap-2">
                <Typography type="text-md" weight="medium">
                  Date
                </Typography>
                <div className="flex flex-wrap gap-2">
                  <DatePicker value={dateFrom} onChange={setDateFrom} clearable={true} />
                  <DatePicker
                    value={dateTo}
                    onChange={setDateTo}
                    disabledIntervals={dateFrom && [Interval.fromDateTimes(DateTime.fromMillis(0), dateFrom)]}
                    clearable={true}
                  />
                </div>
              </div>
            )}

            {disabledFilters?.indexOf('daysPastDue') < 0 && (
              <div className="flex flex-col gap-2">
                <Typography type="text-md" weight="medium">
                  # of Days Past Due
                </Typography>
                <div className="w-25 w-fit">
                  <SelectForm
                    options={daysPastDueOptions}
                    onChange={(c) => {
                      setDaysPastDue(c?.value);
                      if (c?.value !== -1) {
                        setDueDateFrom(undefined);
                        setDueDateTo(undefined);
                      }
                    }}
                    getOptionLabel={(c) => c.label}
                    getOptionValue={(c) => `${c.value}`}
                    isClearable={true}
                    placeholder="# of Days Past Due"
                    value={daysPastDueOptions.find((c) => c.value === daysPastDue)}
                  />
                </div>
              </div>
            )}
            {disabledFilters?.indexOf('dueDate') < 0 &&
              ((disabledFilters?.indexOf('daysPastDue') < 0 && daysPastDue === -1) ||
                disabledFilters?.indexOf('daysPastDue') >= 0) && (
                <div className="flex flex-col gap-2">
                  <Typography type="text-md" weight="medium">
                    Due Date
                  </Typography>
                  <div className="flex flex-wrap gap-2">
                    <DatePicker value={dueDateFrom} onChange={setDueDateFrom} clearable={true} />
                    <DatePicker
                      value={dueDateTo}
                      onChange={setDueDateTo}
                      disabledIntervals={dueDateFrom && [Interval.fromDateTimes(DateTime.fromMillis(0), dueDateFrom)]}
                      clearable={true}
                    />
                  </div>
                </div>
              )}

            {disabledFilters?.indexOf('location') < 0 && (
              <div className="flex flex-col gap-2 w-full">
                <Typography type="text-md" weight="medium">
                  Location
                </Typography>
                <div className="flex gap-2 w-full">
                  <SelectForm
                    className="w-full break-all"
                    defaultValue={selectedLocation}
                    options={locations}
                    getOptionLabel={(l) => l.name}
                    getOptionValue={(l) => l.id.toString()}
                    onChange={(l) => setSelectedLocation(l || undefined)}
                    isClearable={true}
                    menuPlacement="bottom"
                    placeholder="Select"
                  />
                </div>
              </div>
            )}

            {disabledFilters?.indexOf('customer') < 0 && (
              <div className="flex flex-col gap-2 w-full">
                <Typography type="text-md" weight="medium">
                  Customer
                </Typography>
                <div className="flex gap-2 w-full">
                  <UserSearch locations={locations} selectedUser={selectedUser} onSelectUser={setSelectedUser} />
                </div>
              </div>
            )}

            {disabledFilters?.indexOf('address') < 0 && (!!searchRequest.customerId || !!selectedUser) && (
              <div className="flex flex-col gap-2 w-full">
                <Typography type="text-md" weight="medium">
                  Address
                </Typography>
                <UserAddressSearch
                  userId={searchRequest.customerId || selectedUser?.id}
                  selectedAddress={selectedAddress}
                  onSelectAddress={setSelectedAddress}
                />
              </div>
            )}

            {disabledFilters?.indexOf('status') < 0 && (
              <div className="flex flex-col gap-2 w-full">
                <Typography type="text-md" weight="medium">
                  Status
                </Typography>
                <div className="flex gap-2 w-full">
                  <SelectForm
                    className="w-full break-all"
                    value={selectedInvoiceStatus?.map((s) => {
                      return { value: s, label: s };
                    })}
                    options={INVOICE_STATUSES.map((s) => {
                      return { value: s, label: s };
                    })}
                    onChange={(s) => setSelectedInvoiceStatus(s.flatMap((s) => s.value))}
                    isClearable={true}
                    menuPlacement="bottom"
                    placeholder="Select"
                    isMulti={true}
                  />
                </div>
              </div>
            )}
            {disabledFilters?.indexOf('orderStatus') < 0 && (
              <div className="flex flex-col gap-2 w-full">
                <Typography type="text-md" weight="medium">
                  Order Status
                </Typography>
                <div className="flex gap-2 w-full">
                  <SelectForm
                    className="w-full break-all"
                    value={selectedOrderStatus && { value: selectedOrderStatus, label: selectedOrderStatus }}
                    options={WORKFLOW_STATUSES.map((s) => {
                      return { value: s, label: s };
                    })}
                    onChange={(s) => setSelectedOrderStatus(s?.value)}
                    isClearable={true}
                    menuPlacement="bottom"
                    placeholder="Select"
                  />
                </div>
              </div>
            )}
            {disabledFilters?.indexOf('onAccountStatus') < 0 && (
              <div className="flex flex-col gap-2 w-full">
                <Typography type="text-md" weight="medium">
                  On Account Status
                </Typography>
                <div className="flex gap-2 w-full">
                  <SelectForm
                    className="w-full break-all"
                    value={onAccountOptions.find((s) => selectedOnAccountStatus === s.value)}
                    options={onAccountOptions}
                    onChange={(s) => setSelectedOnAccountStatus(s?.value)}
                    isClearable={true}
                    menuPlacement="bottom"
                    placeholder="Select"
                  />
                </div>
              </div>
            )}
          </div>
        </FiltersModal>
      )}
    </div>
  );
};
